import React from "react";
import { PrimaryBlueButton, Severity } from "@velaro/velaro-component-lib";
import { Toggle } from "@/components/Toggle";
import useSubscriptions from "@/hooks/useSubscriptions";
import useApi from "@/hooks/useApi";
import useToastAlert from "@/hooks/useToastAlert";
import { SiteSettings } from "../types";

interface AccountSecuritySettingsProps {
  siteSettings: SiteSettings;
  setSiteSettings: (updater: React.SetStateAction<SiteSettings>) => void;
  isVisible: (text: string) => boolean;
}

export default function AccountSecuritySettings({
  isVisible
}: AccountSecuritySettingsProps) {
  const { subscription } = useSubscriptions();
  const api = useApi();
  const { displayToast } = useToastAlert();

  const [enableSessionTimeout, setEnableSessionTimeout] = React.useState(
    subscription?.enableSessionTimeout ?? false
  );
  const [sessionTimeoutMinutes, setSessionTimeoutMinutes] = React.useState(
    subscription?.sessionTimeoutMinutes ?? 60
  );
  const [enableConcurrentSessionLimit, setEnableConcurrentSessionLimit] = React.useState(
    subscription?.enableConcurrentSessionLimit ?? false
  );
  const [maxConcurrentSessions, setMaxConcurrentSessions] = React.useState(
    subscription?.maxConcurrentSessions ?? 3
  );
  const [enableAgentToAgent, setEnableAgentToAgent] = React.useState(
    subscription?.enableAgentToAgent ?? true
  );
  const [saving, setSaving] = React.useState(false);

  // Sync from subscription once it loads
  React.useEffect(() => {
    if (subscription) {
      setEnableSessionTimeout(subscription.enableSessionTimeout ?? false);
      setSessionTimeoutMinutes(subscription.sessionTimeoutMinutes ?? 60);
      setEnableConcurrentSessionLimit(subscription.enableConcurrentSessionLimit ?? false);
      setMaxConcurrentSessions(subscription.maxConcurrentSessions ?? 3);
      setEnableAgentToAgent(subscription.enableAgentToAgent ?? true);
    }
  }, [subscription]);

  async function save() {
    setSaving(true);
    try {
      const response = await api.admin.post("SecuritySettings", {
        enableSessionTimeout,
        sessionTimeoutMinutes,
        enableConcurrentSessionLimit,
        maxConcurrentSessions,
        enableAgentToAgent
      });
      if (response.ok) {
        displayToast(Severity.Success, "Security settings saved");
      } else {
        displayToast(Severity.Error, "Failed to save security settings");
      }
    } catch {
      displayToast(Severity.Error, "Failed to save security settings");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="max-w-2xl">
      {isVisible("session timeout") && (
        <div className="mb-8 p-5 border border-slate-200 rounded-lg bg-white">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h3 className="text-sm font-medium text-slate-800">Session Timeout</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Auto-logout inactive agents after a set period
              </p>
            </div>
            <Toggle
              toggled={enableSessionTimeout}
              changeToggle={() => setEnableSessionTimeout(!enableSessionTimeout)}
            />
          </div>

          {enableSessionTimeout && (
            <div className="mt-4 pt-4 border-t border-slate-100">
              <label className="block text-xs font-medium text-slate-700 mb-1">
                Logout after (minutes)
              </label>
              <select
                className="border border-slate-300 rounded px-3 py-1.5 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
                value={sessionTimeoutMinutes}
                onChange={(e) => setSessionTimeoutMinutes(Number(e.target.value))}
              >
                <option value={30}>30 minutes</option>
                <option value={60}>60 minutes</option>
                <option value={90}>90 minutes</option>
                <option value={120}>120 minutes</option>
              </select>
              <p className="text-xs text-slate-400 mt-2">
                Agents who have no chat activity for this period will be automatically logged out.
              </p>
            </div>
          )}
        </div>
      )}

      {isVisible("concurrent session") && (
        <div className="mb-8 p-5 border border-slate-200 rounded-lg bg-white">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h3 className="text-sm font-medium text-slate-800">Concurrent Session Limit</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Restrict how many active logins each agent can have at once
              </p>
            </div>
            <Toggle
              toggled={enableConcurrentSessionLimit}
              changeToggle={() => setEnableConcurrentSessionLimit(!enableConcurrentSessionLimit)}
            />
          </div>

          {enableConcurrentSessionLimit && (
            <div className="mt-4 pt-4 border-t border-slate-100">
              <label className="block text-xs font-medium text-slate-700 mb-1">
                Max concurrent sessions per agent
              </label>
              <select
                className="border border-slate-300 rounded px-3 py-1.5 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
                value={maxConcurrentSessions}
                onChange={(e) => setMaxConcurrentSessions(Number(e.target.value))}
              >
                {[1, 2, 3, 4, 5].map((n) => (
                  <option key={n} value={n}>
                    {n} {n === 1 ? "session" : "sessions"}
                  </option>
                ))}
              </select>
              <p className="text-xs text-slate-400 mt-2">
                Prevents multiple people sharing one agent login. When the limit is reached, the
                oldest session is logged out.
              </p>
            </div>
          )}
        </div>
      )}

      {isVisible("agent to agent coaching shadow") && (
        <div className="mb-8 p-5 border border-slate-200 rounded-lg bg-white">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h3 className="text-sm font-medium text-slate-800">Agent-to-Agent Messaging & Coaching</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Allow agents to shadow conversations, send internal coaching notes, and message each other
              </p>
            </div>
            <Toggle
              toggled={enableAgentToAgent}
              changeToggle={() => setEnableAgentToAgent(!enableAgentToAgent)}
            />
          </div>
          {enableAgentToAgent && (
            <p className="text-xs text-slate-400 mt-2">
              Supervisors can silently shadow live conversations and send internal notes visible only to the assigned agent. Agents can also message peers via the Agent Chat panel.
            </p>
          )}
          {!enableAgentToAgent && (
            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
              Shadowing, internal coaching notes, and agent-to-agent chat will be disabled for all agents in your account.
            </div>
          )}
        </div>
      )}

      <div className="mt-2">
        <PrimaryBlueButton
          label={saving ? "Saving..." : "Save"}
          onClick={save}
        />
      </div>
    </div>
  );
}
